import { EyeIcon, EyeOffIcon } from 'lucide-react-native';
import React, { forwardRef, useEffect, useState } from 'react';
import { type Input as BaseInput } from 'tamagui';

import { formatWithMask, SSN_MASK } from '../../utils/maskUtils';
import { Input, type InputProps } from './Input';

export interface SSNInputProps extends Omit<
    InputProps,
    'onChangeText' | 'value' | 'rightIcon' | 'onPressIcon' | 'secureTextEntry'
> {
    value?: string;
    onChangeText?: (formattedValue: string) => void;
    showValue?: boolean;
    allowToggle?: boolean;
}

export const SSNInput = forwardRef<BaseInput, SSNInputProps>(
    (
        {
            value,
            onChangeText,
            placeholder = '123-45-6789',
            inputMode = 'numeric',
            keyboardType = 'number-pad',
            autoComplete = 'off',
            showValue = false,
            allowToggle = true,
            onFocus,
            onBlur,
            ...props
        },
        ref,
    ) => {
        const [isVisible, setIsVisible] = useState(showValue);

        useEffect(() => {
            setIsVisible(showValue);
        }, [showValue]);

        const handleChangeText = (text: string) => {
            const digitsOnly = text.replace(/[^\d]/g, '');
            const { masked } = formatWithMask(SSN_MASK, digitsOnly);
            onChangeText?.(masked);
        };

        const handleToggleVisibility = () => {
            setIsVisible((prev) => !prev);
        };

        return (
            <Input
                ref={ref}
                value={value}
                onChangeText={handleChangeText}
                placeholder={placeholder}
                inputMode={inputMode}
                keyboardType={keyboardType}
                autoComplete={autoComplete}
                secureTextEntry={!isVisible}
                onFocus={onFocus}
                onBlur={onBlur}
                rightIcon={
                    allowToggle ? (isVisible ? EyeIcon : EyeOffIcon) : undefined
                }
                onPressIcon={allowToggle ? handleToggleVisibility : undefined}
                {...props}
            />
        );
    },
);
